import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Fade, Icons, SkeletonText } from 'utoniq-ui'

import {
  ArtistCampaignRankingFragment,
  CampaignAcquisitionType,
  CampaignArtistRanker,
  CampaignDetailFragment,
  CampaignRankingType,
  CampaignUserRanker,
  Maybe,
  UserCampaignRankingFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  RankingUserData,
  RankingUserDataSkeleton,
} from '@/components/organisms'
import { Segment } from '@/components/organisms/functionals'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  campaignArtistRankGetting: boolean
  campaignUserRankGetting: boolean
  campaign: Maybe<CampaignDetailFragment>
  campaignArtistRanking: Maybe<ArtistCampaignRankingFragment>
  campaignUserRanking: Maybe<UserCampaignRankingFragment>
}

export type Action = {
  onClickTab: (isArtist: boolean) => Promise<void>
  onFetchCampaignRanking: (rankingType: string) => Promise<void>
}

type BannerCellProps = {
  image: string
  loading: State['loading']
}

type DescriptionCellProps = {
  campaign: Maybe<CampaignDetailFragment>
  campaignRanking: Maybe<
    ArtistCampaignRankingFragment | UserCampaignRankingFragment
  >
  loading: State['loading']
}

type CampaignRankingCellProps = {
  loading: State['loading']
  campaignRanking: Maybe<
    ArtistCampaignRankingFragment | UserCampaignRankingFragment
  >
  isArtist?: boolean
}

export const CampaignDetailTemplate: Template<State, Action> = ({
  state,
  onClickTab,
  onFetchCampaignRanking,
}) => {
  const { t } = useTranslation('campaignDetail')
  const { routerNames, generatePath, routerPages } = useRouter()
  const [value, setValue] = useState<number>(0)

  useEffect(() => {
    if (
      state.campaign?.rankingType === CampaignRankingType.User &&
      state.campaignUserRanking
    ) {
      setValue(1)
    }
  }, [state.campaign?.rankingType, state.campaignUserRanking])

  useEffect(() => {
    ;(async () => {
      if (!state.campaign?.rankingType) {
        return
      }
      await onFetchCampaignRanking(state.campaign.rankingType)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.campaign?.rankingType])

  const handleChangeValue = async (newValue: number | null) => {
    if (newValue === null) {
      return
    }
    setValue(newValue)
    await onClickTab(!!newValue)
  }

  return (
    <BaseLayout
      header={{
        back: true,
        defaultBackPage: generatePath(routerPages.GlobalEvents, {
          tab: 'campaign',
        }),
        title: state.campaign?.title,
      }}
    >
      <Head title={routerNames.CampaignDetail} />
      <Fade>
        <>
          <BannerCell
            image={state.campaign?.thumbnail?.mediumUri ?? ''}
            loading={state.loading}
          />
          <DescriptionCell
            campaign={state.campaign}
            campaignRanking={
              state.campaignArtistRanking || state.campaignUserRanking
            }
            loading={state.loading}
          />
          <Box sx={{ px: 5 }}>
            <Segment
              labels={[t('artist'), t('user')]}
              value={value}
              onChange={handleChangeValue}
              hiddenLabel={
                state.campaign?.rankingType !== 'both' && !state.loading
              }
            >
              <CampaignRankingCell
                loading={state.loading || state.campaignArtistRankGetting}
                campaignRanking={state.campaignArtistRanking}
                isArtist
              />
              <CampaignRankingCell
                loading={state.loading || state.campaignUserRankGetting}
                campaignRanking={state.campaignUserRanking}
              />
            </Segment>
          </Box>
        </>
      </Fade>
    </BaseLayout>
  )
}

const BannerCell: Cell<BannerCellProps> = ({ image, loading }) => {
  if (loading) {
    return (
      <Skeleton variant="rectangular" sx={{ width: '100%', height: 212 }} />
    )
  }

  if (!image) {
    return null
  }

  return (
    <CellFrame
      sx={{
        width: '100%',
        height: 212,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundImage: `url('${image}')`,
      }}
    />
  )
}

const DescriptionCell: Cell<DescriptionCellProps> = ({
  campaign,
  campaignRanking,
  loading,
}) => {
  const { t } = useTranslation('campaignDetail')
  const acquisitionMethod = useMemo<string>(() => {
    // check condition if there are no acquisitionPoints in the campaign then return empty string
    if (!campaign?.acquisitionPoints?.length) {
      return ''
    }
    // check if ItemRarity exists in array of types ['item_rarity_common', 'item_rarity_rare', 'item_rarity_superrare'] ?
    const hasItemRarity = campaign.acquisitionPoints.some(
      ({ type }) =>
        [
          CampaignAcquisitionType.ItemRarityRare,
          CampaignAcquisitionType.ItemRarityCommon,
          CampaignAcquisitionType.ItemRaritySuperrare,
        ].indexOf(type) >= 0
    )
    if (hasItemRarity) {
      // if ItemRarity exists in an array of types ['item_rarity_common', 'item_rarity_rare', 'item_rarity_superrare'],
      // the string '保有カードのレアリティに基づく獲得ポイント' is returned.
      return t('earnedPoints')
    }
    // if not of the type above, will return 'ガチャを回す {{number}}pt'
    return campaign.acquisitionPoints
      .map(({ type, point }) =>
        type === CampaignAcquisitionType.GachaRoll
          ? `${t('gachaRoll', { number: point })} `
          : t('itemBuy', { number: point })
      )
      .join(' ')
  }, [campaign?.acquisitionPoints, t])

  if (loading) {
    return (
      <CellFrame sx={{ px: 5 }}>
        <Stack sx={{ py: 6, px: 4 }} spacing={2}>
          <SkeletonText variant="medium" width="25%" />
          <SkeletonText variant="medium" width="75%" />
          <SkeletonText variant="medium" width="50%" />
          <SkeletonText variant="medium" width="25%" />
        </Stack>
      </CellFrame>
    )
  }

  return (
    <Box sx={{ px: 7, py: 5 }}>
      <Accordion
        sx={{
          p: 4,
          bgcolor: 'transparent',
          backgroundImage: 'unset',
          border: '1px solid',
          borderColor: 'grey.100',
          '&:last-of-type, &:first-of-type': {
            borderRadius: 2,
          },
        }}
      >
        <AccordionSummary expandIcon={<Icons.DownArrow fontSize="medium" />}>
          <Stack>
            <Typography variant="body2">
              {t('holdingPeriod')}：{campaign?.formattedStartedAt} 〜{' '}
              {campaign?.formattedEndedAt}
            </Typography>
            <Typography variant="body2">
              {t('acquisitionMethod')}：{acquisitionMethod}
            </Typography>
            <Typography variant="body2">{t('aggregationDuration')}</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mr: 6, mb: 4 }}>
            {campaign?.description}
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

const CampaignRankingCell: Cell<CampaignRankingCellProps> = ({
  loading,
  campaignRanking,
}) => {
  const { t } = useTranslation('campaignDetail')
  const { analytics } = useAnalytics()

  const getUserIdOrArtistId = useCallback(
    (ranking?: Maybe<CampaignArtistRanker | CampaignUserRanker>) => {
      if (!ranking) {
        return {}
      }
      if ('artistId' in ranking) {
        return { artistId: ranking.artistId }
      }
      if ('userId' in ranking) {
        return { userId: ranking.userId }
      }
      return {}
    },
    []
  )

  // check condition for render loading
  if (loading && campaignRanking === null) {
    return (
      <CellFrame>
        <Stack spacing={2}>
          <Box sx={{ py: 3 }}>
            <RankingUserDataSkeleton isSolo />
          </Box>
          <Box>
            {Array.from(Array(4).keys()).map(key => (
              <RankingUserDataSkeleton key={key} />
            ))}
          </Box>
        </Stack>
      </CellFrame>
    )
  }
  // check condition for render empty
  if (!loading && campaignRanking?.ranker?.length === 0) {
    return (
      <CellFrame sx={{ px: 5, py: 6 }}>
        <Typography variant="body1" color="text.secondary" align="center">
          {t('rankerEmptyMessage')}
        </Typography>
      </CellFrame>
    )
  }
  return (
    <CellFrame>
      <Stack spacing={2}>
        {campaignRanking?.myRank && (
          <Box sx={{ py: 3 }}>
            <RankingUserData
              isSolo
              ranker={campaignRanking.myRank}
              onClick={() => {
                const { artistId, userId } = getUserIdOrArtistId(
                  campaignRanking?.myRank
                )
                analytics?.track('click_select', {
                  applicationKey: config.applicationKey,
                  [artistId ? 'artistId' : 'userId']: artistId ?? userId,
                  name: 'campaignDetail',
                  tab: artistId ? 'artist' : 'user',
                })
              }}
            />
          </Box>
        )}
        <Box>
          {campaignRanking?.ranker.map((ranker, index) => (
            <Fade key={index}>
              <RankingUserData
                ranker={ranker}
                onClick={() => {
                  const { artistId, userId } = getUserIdOrArtistId(ranker)
                  analytics?.track('click_select', {
                    applicationKey: config.applicationKey,
                    [artistId ? 'artistId' : 'userId']: artistId ?? userId,
                    name: 'campaignDetail',
                    tab: artistId ? 'artist' : 'user',
                  })
                }}
              />
            </Fade>
          ))}
        </Box>
      </Stack>
    </CellFrame>
  )
}
